<template>
  <div
    v-if="isLoading"
    class="d-flex w-100 h-100 justify-content-center align-items-center"
  >
    <div class="loading-logo">
      <img
        src="/logo.svg"
        alt="Logo"
        width="80"
        height="80"
      >
    </div>
  </div>

  <div
    v-else
    class="auth-wrapper auth-v2"
  >
    <b-row class="auth-inner m-0 auth-logo-container signup-bg-color custom-height">
      <!-- App logo -->
      <b-link class="new-app-logo">
        <app-logo-svg />
      </b-link>

      <!-- Left Content -->
      <b-col
        id="auth-left-container"
        lg="6"
        class="d-none d-lg-flex align-items-center"
      >
        <div class="w-100 h-100 d-lg-flex align-items-center justify-content-center">
          <app-video is-v2 />
        </div>
      </b-col>

      <!-- Right/Register Section -->
      <b-col
        lg="6"
        class="right-section-wrapper"
      >
        <div
          class="top-nav-wrapper"
          :class="{ 'only-back-btn-wrapper': !showProgressBar && showBackButton }"
        >
          <!-- Stepper -->
          <div
            v-if="showProgressBar"
            class="stepper-section"
          >
            <div
              v-for="slide in filteredSlides"
              :key="slide.name"
              class="stepper-item"
              :style="{ width: 100 / filteredSlides.length + '%' }"
              :class="{ active: isActive(slide.name) }"
            />
          </div>

          <!-- Back Button -->
          <div class="back-button-wrapper">
            <div
              v-if="showBackButton"
              class="back-button"
              @click="handleBack"
            >
              <arrow-left-icon />
            </div>
          </div>
        </div>

        <!-- Router Outlet -->
        <div
          class="child-routes"
          :class="{
            'child-route-with-back-btn': showBackButton,
            'only-back-btn-route': !showProgressBar && showBackButton,
          }"
        >
          <router-view />
        </div>

        <!-- Sign in Link -->
        <div class="signin-link-wrapper">
          <p class="signin-text">
            Already have an account?
            <router-link
              :to="{ name: 'auth-login' }"
              class="signin-link"
            >
              Sign in
            </router-link>
          </p>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
 BRow, BLink, BCol,
} from 'bootstrap-vue'
import AppLogoSvg from '@/@core/assets/svg-components/AppLogoSvg.vue'
import pages, { AUTH_REGISTER_V2 } from '@/router/routes/pages'
import AppVideo from '@/assets/videos/AppVideo.vue'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import ArrowLeftIcon from '@/@core/assets/svg-components/ArrowLeftIcon.vue'
import { GET_COUNTRY_CODE } from '@/store/modules/register.module'
import { AUTH_REGISTER_V2_CHILDREN, getActiveStepFlow } from '@/router/routes/utils'
import { verifyAllSteps } from './child-routes/verifySteps'

export default {
  name: 'Signup',
  components: {
    BRow,
    BLink,
    BCol,
    AppLogoSvg,
    AppVideo,
    ArrowLeftIcon,
  },
  data() {
    return {
      isLoading: true,
      COMPLETE_INFO: AUTH_REGISTER_V2_CHILDREN.COMPLETE_INFO,
      CONFIRM: AUTH_REGISTER_V2_CHILDREN.CONFIRM,
      SET_OTP: AUTH_REGISTER_V2_CHILDREN.SET_OTP,
      SET_EMAIL: AUTH_REGISTER_V2_CHILDREN.SET_EMAIL,
    }
  },
  computed: {
    showBackButton() {
      const current = this.$route?.name
      return ![this.CONFIRM, this.COMPLETE_INFO, this.SET_EMAIL].includes(current)
    },
    showProgressBar() {
      const current = this.$route?.name
      return ![this.CONFIRM, this.SET_OTP, this.SET_EMAIL].includes(current)
    },
    slides() {
      return pages.find(routeObj => routeObj.name === AUTH_REGISTER_V2).children
    },
    filteredSlides() {
      // Get the keys of currently active steps
      const activeStepKeys = getActiveStepFlow().map(step => step.key)
      // Exclude these steps from the stepper
      const exclude = [this.CONFIRM, this.SET_OTP, this.SET_EMAIL]
      // Filter slides: only those that are active, and not in the exclude list
      return this.slides.filter(
        slide => activeStepKeys.includes(slide.name)
          && !exclude.includes(slide.name),
      )
    },
  },
  async created() {
    this.$store.dispatch(GET_COUNTRY_CODE)
    const invalidStep = await verifyAllSteps(this.$router.push)
    if (invalidStep) {
      this.$router.push({ name: invalidStep }).catch(() => {})
    }
    this.isLoading = false
  },
  mounted() {
    analytics.track(constants.TRACKS.PAGES.SIGN_UP, {
      page_name: 'Sign up',
      timestamp: Date.now(),
    })
  },
  methods: {
    handleBack() {
      const meta = this.$route.meta
      const stepNumber = meta.stepNumber
      const prevRoute = this.slides.find(route => route.meta.stepNumber === stepNumber - 1)
      if (prevRoute) {
        this.$router.push({ name: prevRoute.name })
      }
    },
    isActive(routeName) {
      return this.$route.name === routeName
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/vue/pages/page-auth.scss';
@import '~@core/scss/vue/pages/signup/signup-page.scss';
@import '~@core/scss/vue/pages/signup/kp-signup.scss';

.new-app-logo {
  position: absolute;
  top: 76px;
  left: 40px;
  z-index: 2;

}

#auth-left-container {
  height: -webkit-fill-available;
}

.right-section-wrapper {
  padding: 3.3vw 6.6vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: fit-content;
  height: -webkit-fill-available;
}

.top-nav-wrapper {
  display: flex;
  flex-direction: column;
  max-height: 18vh;
  height: -webkit-fill-available;
  gap: 15px;
  justify-content: space-between;
}

.top-nav-wrapper.only-back-btn-wrapper {
  justify-content: flex-end;
  width: 100%;
  max-width: 528px;
  .back-button {
    margin-bottom: 0;
  }
}

.stepper-section {
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 34.5vw;
  max-width: 500px;
  height: fit-content;
  gap: 8px;

  .stepper-item {
    box-sizing: border-box;
    height: 10px;
    border-radius: 8px;
    background: #C7D3EB;

    &.active {
      background: #069CDF;
    }
  }
}

.back-button-wrapper {
  width: 100%;
  max-width: 528px;
}

.back-button {
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #343A40;
  align-self: baseline;
  width: fit-content;
  margin-bottom: 1rem;
}

.child-routes {
  width: 100%;
}

.child-route-with-back-btn {
  margin-top: 8vh;
}

.only-back-btn-route.child-route-with-back-btn {
  margin-top: 4vh;
}

.signin-link-wrapper {
  width: 100%;
  max-width: 528px;
  margin-top: auto;
}

.signin-text {
  color: #6C757D;
  font-size: 14px;
  line-height: 20px;
}

.signin-link {
  color: #069CDF;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

@media screen and (max-width: 991px) {
  .back-button-wrapper {
    max-width: 528px;
  }
  .stepper-section {
    width: 50vw;
    margin-top: 100px;
  }
  .new-app-logo {
    top: 40px;
    svg path {
      fill: $app-logo-color-md !important;
    }
  }
  .child-route-with-back-btn {
    margin-top: 8vh;
  }
}

@media screen and (max-width: 660px) {
  .right-section-wrapper {
    padding: 24px 48px;
  }
  .stepper-section {
    min-width: 280px;
    width: min(50vw, calc(100% - 96px));
  }
}

@media screen and (max-width: 425px) {
  .right-section-wrapper {
    padding: 24px;
  }
  .stepper-section {
    width: max(calc(100% - 96px), 100%);
  }
}

@media screen and (max-width: 340px) {
  .stepper-section {
    width: 100%;
    min-width: 100%;
  }
}

@media screen and (max-height: 715px) {
  .custom-height {
    min-height: 100% !important;
  }
  .stepper-section {
    margin-top: 20px;
  }
}

</style>
